export interface CapabilityDefinition {
  value: string;
  label: string;
  group: string;
  groupLabel: string;
  description: string;
  defaultRateType: string;
  defaultUnit: string;
  supportsTurnaround?: boolean;
  supportsRush?: boolean;
  supportsPricing?: boolean;
  supportsQuantityRange?: boolean;
  supportsFileTypes?: boolean;
  supportsDecorationMethods?: boolean;
  supportsLogisticsOptions?: boolean;
  supportsDataEntryOptions?: boolean;
}

export const CAPABILITY_CATALOGUE: CapabilityDefinition[] = [
  {
    value: 'artwork-proof',
    label: 'Artwork Proof',
    group: 'design',
    groupLabel: 'Design & Artwork',
    description: 'Create a customer-ready proof showing artwork placement, sizing, colours, and product details.',
    defaultRateType: 'per_proof',
    defaultUnit: 'per proof',
    supportsTurnaround: true,
    supportsRush: true,
    supportsPricing: true,
    supportsFileTypes: true,
  },
  {
    value: 'vector-conversion',
    label: 'Vector Conversion',
    group: 'design',
    groupLabel: 'Design & Artwork',
    description: 'Convert low-resolution or raster artwork into production-ready vector artwork.',
    defaultRateType: 'per_design',
    defaultUnit: 'per design',
    supportsTurnaround: true,
    supportsRush: true,
    supportsPricing: true,
    supportsFileTypes: true,
  },
  {
    value: 'digitizing',
    label: 'Embroidery Digitizing',
    group: 'design',
    groupLabel: 'Design & Artwork',
    description: 'Convert artwork into a machine-ready embroidery file.',
    defaultRateType: 'per_design',
    defaultUnit: 'per design',
    supportsTurnaround: true,
    supportsRush: true,
    supportsPricing: true,
    supportsFileTypes: true,
  },
  {
    value: 'proof-mockup',
    label: 'Proof Mockup',
    group: 'design',
    groupLabel: 'Design & Artwork',
    description: 'Create a visual product mockup for customer review before production.',
    defaultRateType: 'per_mockup',
    defaultUnit: 'per mockup',
    supportsTurnaround: true,
    supportsRush: true,
    supportsPricing: true,
  },
  {
    value: 'mockups',
    label: 'Product Mockups',
    group: 'design',
    groupLabel: 'Design & Artwork',
    description: 'Produce realistic product visuals for presentations, approvals, or sales use.',
    defaultRateType: 'per_mockup',
    defaultUnit: 'per mockup',
    supportsTurnaround: true,
    supportsRush: true,
    supportsPricing: true,
  },
  {
    value: 'artwork',
    label: 'General Artwork Services',
    group: 'design',
    groupLabel: 'Design & Artwork',
    description: 'General artwork creation, cleanup, layout, and production preparation.',
    defaultRateType: 'hourly',
    defaultUnit: 'hourly',
    supportsTurnaround: true,
    supportsRush: true,
    supportsPricing: true,
    supportsFileTypes: true,
  },
  {
    value: 'decoration-production',
    label: 'Decoration Production',
    group: 'decoration',
    groupLabel: 'Decoration & Production',
    description: 'Produce decorated goods using approved production methods.',
    defaultRateType: 'per_unit',
    defaultUnit: 'per unit',
    supportsTurnaround: true,
    supportsRush: true,
    supportsPricing: true,
    supportsQuantityRange: true,
    supportsDecorationMethods: true,
  },
  {
    value: 'logistics',
    label: 'Logistics & Fulfillment',
    group: 'logistics',
    groupLabel: 'Logistics & Fulfillment',
    description: 'Handle shipping, kitting, fulfillment, warehousing, or related logistics services.',
    defaultRateType: 'quoted',
    defaultUnit: 'quoted',
    supportsTurnaround: true,
    supportsPricing: true,
    supportsQuantityRange: true,
    supportsLogisticsOptions: true,
  },
  {
    value: 'data_entry',
    label: 'Data Entry',
    group: 'software',
    groupLabel: 'Software & Administrative Services',
    description: 'Complete structured data entry, order entry, product setup, or administrative work.',
    defaultRateType: 'hourly',
    defaultUnit: 'hourly',
    supportsTurnaround: true,
    supportsPricing: true,
    supportsDataEntryOptions: true,
  },
];

export const CAPABILITY_GROUPS = Array.from(
  CAPABILITY_CATALOGUE.reduce((groups, capability) => {
    if (!groups.has(capability.group)) {
      groups.set(capability.group, capability.groupLabel);
    }
    return groups;
  }, new Map<string, string>())
).map(([value, label]) => ({ value, label }));

export const FILE_TYPE_OPTIONS = ['AI', 'EPS', 'PDF', 'SVG', 'PSD', 'PNG', 'JPG', 'DST', 'EMB'];

export const DECORATION_METHOD_OPTIONS = [
  'Embroidery',
  'Screen Print',
  'Direct to Film',
  'Direct to Garment',
  'Heat Transfer',
  'Sublimation',
  'Laser Engraving',
  'Pad Print',
  'UV Print',
  'Deboss',
  'Emboss',
  'Vinyl',
];

export const LOGISTICS_SERVICE_OPTIONS = ['Kitting', 'Warehousing', 'Pick and pack', 'Blind shipping', 'International shipping'];

export const DATA_ENTRY_TASK_OPTIONS = ['Order entry', 'Product setup', 'CRM updates', 'Proof data cleanup', 'Administrative review'];

export const RATE_TYPE_OPTIONS = [
  { value: 'fixed', label: 'Fixed' },
  { value: 'hourly', label: 'Hourly' },
  { value: 'per_unit', label: 'Per unit' },
  { value: 'per_design', label: 'Per design' },
  { value: 'per_proof', label: 'Per proof' },
  { value: 'per_mockup', label: 'Per mockup' },
  { value: 'quoted', label: 'Quoted' },
];

export const TURNAROUND_UNIT_OPTIONS = [
  { value: 'business_hours', label: 'Business hours' },
  { value: 'business_days', label: 'Business days' },
  { value: 'calendar_days', label: 'Calendar days' },
];

export const findCapabilityDefinition = (value?: string | null): CapabilityDefinition | undefined => (
  CAPABILITY_CATALOGUE.find((capability) => capability.value === value)
);

export const getCapabilityLabel = (value?: string | null): string => findCapabilityDefinition(value)?.label || value || 'Not set';

export const getCapabilityGroupLabel = (value?: string | null): string => (
  CAPABILITY_GROUPS.find((group) => group.value === value)?.label || value || 'Not set'
);
